import { useEffect } from "react";
import { Truck, ShieldCheck, BellRing, LayoutDashboard, Sparkles, Play } from "lucide-react";
import { Reveal, StaggerGroup, StaggerItem } from "../components/site/Motion";
import { SectionLabel } from "../components/site/Bits";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";

const PLAY_URL = "https://play.google.com/store/apps/details?id=com.renewtracker.app";

const documents = [
  "Vehicle insurance",
  "Registration-related documents",
  "PUC / pollution certificates",
  "Fitness certificates",
  "Commercial permits",
  "Driver licences",
  "Employee or driver certifications",
  "Other custom documents with expiry dates",
];

const audiences = [
  "Transport operators",
  "Fleet owners",
  "Logistics businesses",
  "Truck and commercial vehicle operators",
  "Construction companies with vehicles",
  "Businesses managing multiple company vehicles",
];

const helpPoints = [
  { icon: LayoutDashboard, title: "Centralised tracking", desc: "Keep every renewal and expiry date for your fleet in one place instead of scattered spreadsheets or paper records." },
  { icon: BellRing, title: "Timely email & push reminders", desc: "Get notified ahead of each deadline so you have time to act before a document expires." },
  { icon: ShieldCheck, title: "Upcoming, active & expired view", desc: "A clear dashboard shows what's expiring soon, what's active and what has already lapsed." },
  { icon: Sparkles, title: "Custom categories", desc: "Group renewals your own way — by vehicle, category, driver or any other structure that fits your fleet." },
  { icon: Truck, title: "Works across your operation", desc: "Track vehicles, employees, equipment, licences, permits, contracts and subscriptions in the same app." },
];

const faqs = [
  { q: "What is a fleet document expiry reminder?", a: "A fleet document expiry reminder is a notification that alerts you before an important vehicle or fleet document — such as insurance, registration paperwork, pollution certificate, fitness certificate, permit or driver licence — is due to expire, so you have time to plan the renewal." },
  { q: "Which vehicle documents can I track with RenewTracker?", a: "You can track a wide range of vehicle and fleet documents including insurance, registration-related paperwork, PUC/pollution certificates, fitness certificates, commercial permits, driver licences, driver certifications and any other documents with expiry dates using custom categories." },
  { q: "Can I track documents for multiple vehicles?", a: "Yes. RenewTracker is built to centralise renewals across many items, so you can add multiple vehicles and keep every deadline organised in one dashboard." },
  { q: "Can I set reminders before a document expires?", a: "Yes. RenewTracker sends email and push reminders ahead of each expiry date so you are notified in time." },
  { q: "Is RenewTracker available on Android?", a: "Yes. RenewTracker is available now on Google Play. The iOS version is coming soon to the Apple App Store." },
  { q: "Does RenewTracker automatically renew documents?", a: "No. RenewTracker is a tracking and reminder tool. It does not renew documents on your behalf — you remain responsible for completing renewals with the relevant authorities or providers. RenewTracker helps you stay organised and notified in time." },
];

const PlayCTA = ({ className = "", size = "default", testid = "play-cta" }) => {
  const pad = size === "lg" ? "px-6 py-4" : "px-5 py-3.5";
  return (
    <a
      href={PLAY_URL}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testid}
      aria-label="Get RenewTracker on Google Play"
      className={`group inline-flex items-center gap-3 rounded-2xl bg-ink text-white shadow-sm transition-transform duration-200 hover:-translate-y-0.5 ${pad} ${className}`}
    >
      <Play className="h-6 w-6 text-greenaccent fill-greenaccent" strokeWidth={0} />
      <span className="text-left leading-tight">
        <span className="block text-[10px] uppercase tracking-widest text-white/60">Get it on</span>
        <span className="block font-display text-base font-semibold">Google Play</span>
      </span>
      <span className="ml-2 rounded-full bg-greenaccent/20 px-2 py-0.5 text-[10px] font-semibold text-greenaccent">Available Now</span>
    </a>
  );
};

export default function FleetDocumentExpiryReminder() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Fleet Document Expiry Reminder App | RenewTracker";
    const descEl = document.querySelector('meta[name="description"]');
    const prevDesc = descEl ? descEl.getAttribute("content") : null;
    if (descEl) descEl.setAttribute("content", "Track vehicle and fleet document expiry dates with RenewTracker. Get timely reminders for insurance, RC, PUC, permits, licences and other renewal deadlines.");

    const ldWebPage = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: "Fleet Document Expiry Reminder App | RenewTracker",
      description: "Track vehicle and fleet document expiry dates with RenewTracker. Get timely reminders for insurance, RC, PUC, permits, licences and other renewal deadlines.",
      url: "https://renewtracker.site/fleet-document-expiry-reminder",
      isPartOf: { "@type": "WebSite", name: "RenewTracker", url: "https://renewtracker.site" },
    };
    const ldFaq = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    };
    const s1 = document.createElement("script");
    s1.type = "application/ld+json";
    s1.dataset.page = "fleet-reminder-webpage";
    s1.textContent = JSON.stringify(ldWebPage);
    const s2 = document.createElement("script");
    s2.type = "application/ld+json";
    s2.dataset.page = "fleet-reminder-faq";
    s2.textContent = JSON.stringify(ldFaq);
    document.head.appendChild(s1);
    document.head.appendChild(s2);

    return () => {
      document.title = prevTitle;
      if (descEl && prevDesc !== null) descEl.setAttribute("content", prevDesc);
      s1.remove();
      s2.remove();
    };
  }, []);

  return (
    <main data-testid="fleet-page" className="bg-background pt-28">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-slate-200" data-testid="fleet-hero">
        <div className="grid-lines pointer-events-none absolute inset-0 opacity-60" />
        <div className="mesh-glow pointer-events-none absolute inset-0" />
        <div className="relative mx-auto max-w-4xl px-5 py-20 lg:px-8">
          <Reveal>
            <SectionLabel>For fleet operators</SectionLabel>
            <h1 className="mt-5 font-display text-4xl font-extrabold leading-[1.05] tracking-tighter text-ink sm:text-5xl lg:text-6xl">
              Fleet Document Expiry Reminder
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
              Keep your fleet's important renewal dates in one place. RenewTracker helps businesses track vehicle documents, licences, permits, insurance and other expiry dates and receive timely reminders before deadlines.
            </p>
            <div className="mt-8">
              <PlayCTA testid="fleet-hero-play-cta" size="lg" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Section 2 — Documents you can track */}
      <section className="mx-auto max-w-4xl px-5 py-16 lg:px-8" data-testid="fleet-documents">
        <Reveal>
          <SectionLabel>Vehicle & fleet documents</SectionLabel>
          <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
            Keep every vehicle deadline in one place.
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600">
            Fleet operators may need to keep track of dates for many kinds of vehicle documents. Examples include:
          </p>
        </Reveal>
        <StaggerGroup className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {documents.map((d) => (
            <StaggerItem key={d}>
              <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-greenaccent/10 text-greenaccent">
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <span className="text-sm font-medium text-ink">{d}</span>
              </div>
            </StaggerItem>
          ))}
        </StaggerGroup>
        <p className="mt-6 text-xs italic text-slate-500">
          These are general examples of documents fleet businesses often track. RenewTracker is a reminder tool and does not determine which documents are legally required for your operation.
        </p>
      </section>

      {/* Section 3 — Why it matters */}
      <section className="bg-white py-16" data-testid="fleet-why">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Why it matters</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Why fleet renewal tracking matters.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              Fleet renewals add up quickly, and small oversights can create bigger operational headaches.
            </p>
          </Reveal>
          <div className="mt-8 space-y-4">
            {[
              { t: "Many dates, many vehicles.", d: "Multiple vehicles create many renewal dates that all need to be tracked together." },
              { t: "Spreadsheets and paper can be hard to maintain.", d: "Important dates may be difficult to track using spreadsheets, paper records or scattered reminders." },
              { t: "A missed renewal can be disruptive.", d: "Missing a renewal can create operational or compliance problems for your business." },
            ].map((p, i) => (
              <Reveal key={p.t} delay={i * 0.05}>
                <div className="rounded-2xl border border-slate-200 bg-background p-6">
                  <p className="font-display text-lg font-semibold text-ink">{p.t}</p>
                  <p className="mt-1 text-sm leading-relaxed text-slate-600">{p.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4 — How RenewTracker helps */}
      <section className="py-16" data-testid="fleet-how">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <Reveal className="max-w-2xl">
            <SectionLabel>How it helps</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              How RenewTracker helps fleet operators.
            </h2>
            <p className="mt-4 text-base leading-relaxed text-slate-600">
              RenewTracker centralises fleet renewal reminders and expiry tracking so nothing important slips through the cracks.
            </p>
          </Reveal>
          <StaggerGroup className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {helpPoints.map((h) => (
              <StaggerItem key={h.title}>
                <div className="h-full rounded-3xl border border-slate-200 bg-white p-7">
                  <span className="grid h-11 w-11 place-items-center rounded-2xl bg-brand/5 text-brand">
                    <h.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-5 font-display text-lg font-semibold text-ink">{h.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">{h.desc}</p>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Section 5 — Who is it for */}
      <section className="bg-white py-16" data-testid="fleet-who">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>Who is this for?</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Built for teams that manage vehicles.
            </h2>
          </Reveal>
          <StaggerGroup className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {audiences.map((a) => (
              <StaggerItem key={a}>
                <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-background px-5 py-4">
                  <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-brand/5 text-brand">
                    <Truck className="h-4 w-4" />
                  </span>
                  <span className="text-sm font-medium text-ink">{a}</span>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Section 6 — FAQ */}
      <section className="py-16" data-testid="fleet-faq">
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <Reveal>
            <SectionLabel>FAQ</SectionLabel>
            <h2 className="mt-5 font-display text-3xl font-bold tracking-tight text-ink sm:text-4xl">
              Fleet renewal reminders — questions answered.
            </h2>
          </Reveal>
          <Reveal className="mt-8">
            <Accordion type="single" collapsible className="w-full space-y-3" data-testid="fleet-accordion">
              {faqs.map((f, i) => (
                <AccordionItem
                  key={i}
                  value={`fleet-${i}`}
                  data-testid={`fleet-faq-${i}`}
                  className="overflow-hidden rounded-2xl border border-slate-200 bg-white px-6"
                >
                  <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-ink hover:no-underline">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-[15px] leading-relaxed text-slate-600">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* Section 7 — Final CTA */}
      <section className="pb-24" data-testid="fleet-cta">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-[2.5rem] bg-brand px-8 py-16 text-center shadow-2xl sm:px-16 sm:py-20">
              <div className="mesh-glow pointer-events-none absolute inset-0 opacity-70" />
              <div className="grid-lines pointer-events-none absolute inset-0 opacity-[0.08]" />
              <div className="relative mx-auto max-w-2xl">
                <h2 className="font-display text-3xl font-bold leading-tight tracking-tight text-white sm:text-5xl">
                  Don't wait until a renewal is due.
                </h2>
                <p className="mt-5 text-lg text-white/75">
                  Start tracking your fleet's important renewal dates with RenewTracker.
                </p>
                <div className="mt-9 flex justify-center">
                  <PlayCTA testid="fleet-final-play-cta" size="lg" />
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
